// The phone rendering of a table row — phase 8, DM3.
//
// A six-column table at 375px either crushes its columns or scrolls sideways,
// and sideways is where information goes to hide. Under `sm` the list pages
// hide their tables and render one of these per row instead: primary line,
// badges top-right, a couple of secondary lines, tap → detail — the Zanetto
// Tools mobile plan's card pattern.
//
// The table and the cards are two renderings of the same rows on the same
// page: the table's `.table-wrap` carries `max-sm:hidden`, the cards sit in a
// `CardList` (which is `sm:hidden`), so exactly one of them exists at any
// width and they can never disagree about filtering or order.
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

export function CardList({ children }: { children: ReactNode }) {
  return <ul className="m-0 list-none space-y-2 p-0 sm:hidden">{children}</ul>;
}

export function RowCard({
  title,
  to,
  badges,
  actions,
  children,
}: {
  /** Primary line — the name or number of the thing. */
  title: ReactNode;
  /** When set, the WHOLE card is the link — a 375px-wide tap target, not a
      small link inside one. Leave unset for rows with no detail page. */
  to?: string;
  /** Badge cluster, top-right. */
  badges?: ReactNode;
  /** Action row along the bottom, outside the link so a stray tap on
      Archive can never navigate. */
  actions?: ReactNode;
  /** Secondary lines. Omit empty values rather than printing "—". */
  children?: ReactNode;
}) {
  const head = (
    <div className="flex items-start justify-between gap-3">
      <div className={`min-w-0 text-sm font-medium ${to ? 'text-brand-800' : 'text-gray-900'}`}>
        {title}
      </div>
      {badges && (
        <div className="flex flex-none flex-wrap items-center justify-end gap-1">{badges}</div>
      )}
    </div>
  );
  const body = children ? (
    <div className="mt-1 space-y-0.5 text-sm text-gray-600">{children}</div>
  ) : null;

  return (
    <li className="card overflow-hidden">
      {to ? (
        <Link to={to} className="block p-4 transition-colors hover:bg-brand-50/40 active:bg-brand-50">
          {head}
          {body}
        </Link>
      ) : (
        <div className="p-4">
          {head}
          {body}
        </div>
      )}
      {actions && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-4 py-2">
          {actions}
        </div>
      )}
    </li>
  );
}
